/**
 * Publish History Dialog
 *
 * Modal dialog showing the publish history for the Home Layout Manager.
 * Allows viewing past publishes and rolling back to a previous state.
 *
 * @module HomeLayoutManager/components/PublishHistoryDialog
 */

import React, { useEffect, useCallback, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Box,
  Skeleton,
  Divider,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  History as HistoryIcon,
  Restore as RestoreIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Notes as NotesIcon,
} from '@mui/icons-material';

import { useHomeLayout } from '../HomeLayoutContext';
import { usePublish } from '../hooks';
import type { PublishHistoryItem } from '../types';

/**
 * Format a date string to a human-readable format
 */
function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-ZA', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/**
 * Format relative time from ISO date string
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return formatDateTime(dateString);
}

/**
 * Props for PublishHistoryDialog
 */
interface PublishHistoryDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog closes */
  onClose: () => void;
}

/**
 * History item component
 */
interface HistoryItemProps {
  /** History item data */
  item: PublishHistoryItem;
  /** Whether this is the first (most recent) item */
  isLatest: boolean;
  /** Rollback callback */
  onRollback: (id: string) => void;
  /** Whether rollback is in progress */
  isRollingBack: boolean;
  /** ID of the item currently being rolled back */
  rollingBackId: string | null;
}

function HistoryItem({
  item,
  isLatest,
  onRollback,
  isRollingBack,
  rollingBackId,
}: HistoryItemProps): React.ReactElement {
  const isCurrentlyRollingBack = rollingBackId === item.id;

  return (
    <ListItem
      sx={{
        borderRadius: 1,
        mb: 1,
        bgcolor: isLatest ? 'success.lighter' : 'background.paper',
        border: 1,
        borderColor: isLatest ? 'success.light' : 'divider',
      }}
    >
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" fontWeight={500}>
              {formatDateTime(item.publishedAt)}
            </Typography>
            {isLatest && (
              <Chip
                label="Current"
                size="small"
                color="success"
                variant="filled"
                sx={{ height: 20, fontSize: 10 }}
              />
            )}
          </Box>
        }
        secondary={
          <Box sx={{ mt: 0.5 }}>
            {item.publishedByUser && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {item.publishedByUser.name}
                </Typography>
              </Box>
            )}
            {item.publishNotes && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <NotesIcon sx={{ fontSize: 14, color: 'text.secondary', mt: 0.25 }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontStyle: 'italic',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  "{item.publishNotes}"
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <ScheduleIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.disabled">
                {formatRelativeTime(item.publishedAt)}
              </Typography>
            </Box>
          </Box>
        }
      />
      <ListItemSecondaryAction>
        {!isLatest && (
          <Tooltip title="Rollback to this state">
            <span>
              <IconButton
                edge="end"
                onClick={() => onRollback(item.id)}
                disabled={isRollingBack}
                color="primary"
              >
                {isCurrentlyRollingBack ? (
                  <CircularProgress size={20} />
                ) : (
                  <RestoreIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </ListItemSecondaryAction>
    </ListItem>
  );
}

/**
 * Loading skeleton for history items
 */
function HistorySkeleton(): React.ReactElement {
  return (
    <>
      {[1, 2, 3].map((i) => (
        <Box key={i} sx={{ mb: 1 }}>
          <Skeleton variant="rounded" height={80} />
        </Box>
      ))}
    </>
  );
}

/**
 * Empty state when no history exists
 */
function EmptyHistory(): React.ReactElement {
  return (
    <Box
      sx={{
        textAlign: 'center',
        py: 4,
        px: 2,
      }}
    >
      <HistoryIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
      <Typography variant="body2" color="text.secondary">
        No publish history yet
      </Typography>
      <Typography variant="caption" color="text.disabled">
        History will appear here after your first publish
      </Typography>
    </Box>
  );
}

/**
 * Publish History Dialog Component
 *
 * Shows a list of all previous publishes with the ability to rollback
 * the draft to any previous state.
 */
export default function PublishHistoryDialog({
  open,
  onClose,
}: PublishHistoryDialogProps): React.ReactElement {
  const { refreshDraft } = useHomeLayout();

  const {
    publishHistory,
    isLoadingHistory,
    historyHasMore,
    fetchPublishHistory,
    rollback,
    isPublishing,
  } = usePublish({
    onRollbackSuccess: () => {
      refreshDraft();
      onClose();
    },
  });

  const [rollingBackId, setRollingBackId] = useState<string | null>(null);
  const [historyPage, setHistoryPage] = useState(1);

  // Fetch history when dialog opens
  useEffect(() => {
    if (open) {
      setHistoryPage(1);
      fetchPublishHistory(1);
    }
  }, [open, fetchPublishHistory]);

  // Handle rollback
  const handleRollback = useCallback(
    async (publishId: string) => {
      setRollingBackId(publishId);
      try {
        await rollback(publishId);
      } finally {
        setRollingBackId(null);
      }
    },
    [rollback]
  );

  // Load more history
  const handleLoadMore = useCallback(() => {
    const nextPage = historyPage + 1;
    setHistoryPage(nextPage);
    fetchPublishHistory(nextPage);
  }, [historyPage, fetchPublishHistory]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '80vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <HistoryIcon color="primary" />
        Publish History
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Rolling back will update your draft to match a previous published state.
          You'll need to publish again to make changes live.
        </Alert>

        {isLoadingHistory && publishHistory.length === 0 ? (
          <HistorySkeleton />
        ) : publishHistory.length === 0 ? (
          <EmptyHistory />
        ) : (
          <>
            <List disablePadding>
              {publishHistory.map((item, index) => (
                <HistoryItem
                  key={item.id}
                  item={item}
                  isLatest={index === 0}
                  onRollback={handleRollback}
                  isRollingBack={isPublishing}
                  rollingBackId={rollingBackId}
                />
              ))}
            </List>

            {historyHasMore && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleLoadMore}
                  disabled={isLoadingHistory}
                >
                  {isLoadingHistory ? 'Loading...' : 'Load More'}
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
